import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";

const SupportChatContext = createContext();

export const SupportChatProvider = ({ children }) => {
  // Accessing the centralized maritime uplink socket and tenant scope from AuthContext
  const { socket, user, adminId } = useAuth();
  const userId = user?._id;
  
  const [showSupport, setShowSupport] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Using a ref for showSupport to avoid re-binding the socket listener 
  // every time the chat window toggles.
  const isChatOpen = useRef(showSupport);
  useEffect(() => {
    isChatOpen.current = showSupport;
  }, [showSupport]);

  /**
   * REAL-TIME TELEMETRY LISTENER
   */
  useEffect(() => {
    if (!socket || !userId) return;

    // 1. Handle incoming messages for unread badge logic
    const handleNewMessage = (message) => {
      const isExternalMessage = message.sender !== userId;
      
      if (isExternalMessage && !isChatOpen.current) {
        setUnreadCount((prev) => prev + 1);
      }
    };

    // 2. Handle direct unread updates from the server (Sync with DB)
    const handleUnreadUpdate = (data) => {
      if (data.userId === userId && !isChatOpen.current) {
        setUnreadCount(data.count);
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("unreadUpdate", handleUnreadUpdate);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("unreadUpdate", handleUnreadUpdate);
    };
  }, [socket, userId]);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const openSupportChat = useCallback((id = null) => {
    setConversationId(id);
    setShowSupport(true);
    setUnreadCount(0);
  }, []);

  const closeSupportChat = useCallback(() => {
    setShowSupport(false);
    setTimeout(() => {
      setConversationId(null);
    }, 300);
  }, []);

  return (
    <SupportChatContext.Provider
      value={{ 
        showSupport, 
        setShowSupport, 
        conversationId, 
        setConversationId,
        unreadCount, 
        setUnreadCount, 
        clearUnread,
        openSupportChat, 
        closeSupportChat,
        isSystemOnline: !!socket?.connected,
        tenantId: adminId || user?.adminId
      }}
    >
      {children}
    </SupportChatContext.Provider>
  );
};

export const useSupportChat = () => {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error("useSupportChat must be utilized within a SupportChatProvider");
  }
  return context;
};